import { DragEvent, useRef, useState } from "react"
import { t } from "../../i18n"
import { importPlainJobCodeList, INVALID_JOB_CODE_REASON } from "../../services/jobCodeListImport"
import {
  importSpreadsheet,
  isSpreadsheetFile,
  SpreadsheetImportError,
} from "../../services/spreadsheetImportService"
import { isPlainJobCodeListFile, readDroppedText } from "../../utils/jobCodeList"
import { SpreadsheetImportFailure, SpreadsheetImportResult } from "../../utils/spreadsheetImportResult"

function failureDetail(failure: SpreadsheetImportFailure): string {
  const message = failure.message === INVALID_JOB_CODE_REASON || failure.message === "failed"
    ? (failure.message === INVALID_JOB_CODE_REASON ? t("jobCodes.codeInvalid") : t("jobCodes.importFailed"))
    : failure.message
  return failure.code ? `${failure.code} — ${message}` : message
}

export default function JobSiteCodeImport({
  companyId,
  jobSiteId,
  onImported,
}: {
  companyId: number
  jobSiteId: number
  onImported?: () => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [status, setStatus] = useState("")
  const [failed, setFailed] = useState(false)
  const [failures, setFailures] = useState<SpreadsheetImportFailure[]>([])

  const showResult = (result: SpreadsheetImportResult) => {
    const parts: string[] = []
    if (result.imported > 0) {
      parts.push(t("jobCodes.importAdded", { count: result.imported }))
    }
    if (result.failures.length > 0) {
      parts.push(t("jobCodes.importFailedRows"))
    }
    if (!parts.length) {
      setFailed(true)
      setStatus(t("jobCodes.importEmpty"))
      setFailures([])
      return
    }
    setFailed(result.imported === 0)
    setStatus(parts.join(" "))
    setFailures(result.failures)
    if (result.imported > 0) onImported?.()
  }

  const handleText = async (text: string) => {
    setUploading(true)
    setFailed(false)
    setStatus(t("jobCodes.importing"))
    setFailures([])
    try {
      const result = await importPlainJobCodeList({ companyId, jobSiteId, text })
      showResult(result)
    } catch {
      setFailed(true)
      setStatus(t("jobCodes.importFailed"))
      setFailures([])
    } finally {
      setUploading(false)
    }
  }

  const handleFile = async (file?: File) => {
    if (!file || uploading) return
    if (isSpreadsheetFile(file)) {
      setUploading(true)
      setFailed(false)
      setStatus(t("jobCodes.importing"))
      setFailures([])
      try {
        const result = await importSpreadsheet({
          kind: "job-codes",
          file,
          managementCompanyId: companyId,
          jobSiteId,
        })
        showResult(result)
      } catch (error) {
        setFailed(true)
        setFailures([])
        setStatus(
          error instanceof SpreadsheetImportError && error.issue === "columns"
            ? t("import.missingColumns")
            : t("jobCodes.importFailed"),
        )
      } finally {
        setUploading(false)
        if (inputRef.current) inputRef.current.value = ""
      }
      return
    }
    if (isPlainJobCodeListFile(file) || file.type.startsWith("text/")) {
      const text = await readDroppedText(file)
      await handleText(text)
      if (inputRef.current) inputRef.current.value = ""
      return
    }
    setFailed(true)
    setStatus(t("jobCodes.importInvalidFile"))
    setFailures([])
  }

  const onDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault()
    setDragOver(false)
    const file = event.dataTransfer.files?.[0]
    if (file) {
      void handleFile(file)
      return
    }
    const text = event.dataTransfer.getData("text/plain")
    if (text.trim()) void handleText(text)
  }

  return (
    <div
      onDragOver={(event) => {
        event.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      <label
        className={`mb-3 block cursor-pointer rounded-xl border-2 border-dashed px-4 py-4 text-center font-source ${
          dragOver ? "border-primary bg-secondary-extra-light" : "border-gray-200 bg-secondary-extra-light"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xls,.xlsx,.xlsm,.txt,.csv,.tsv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv"
          className="sr-only"
          disabled={uploading}
          onChange={(event) => void handleFile(event.target.files?.[0])}
        />
        <p className="text-primary-dark">{t("jobCodes.drop")}</p>
        <p className="mt-1 text-sm text-gray-600">{t("jobCodes.dropHelper")}</p>
        <p className="mt-1 text-sm text-gray-600">{t("import.sourceJobCodes")}</p>
      </label>
      {status ? (
        <p className={`mb-2 text-sm ${failed ? "text-red-600" : "text-primary-dark"}`} role="status">
          {status}
        </p>
      ) : null}
      {failures.length > 0 ? (
        <ul className="mb-3 list-disc pl-5 text-sm text-red-700 space-y-1" role="alert">
          {failures.map((failure, index) => (
            <li key={`${failure.row}-${failure.code}-${index}`}>
              {failure.row
                ? t("jobCodes.importRowFailed", { row: failure.row, detail: failureDetail(failure) })
                : failureDetail(failure)}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
